import { useState } from 'react';
import { useTransition } from '@react-spring/core';
import { useFormikContext } from 'formik';
import { format as formatDate } from 'date-fns';
import { noop } from 'lodash';
import PropTypes from 'prop-types';

import { useOutsideClick } from 'hooks';

import Fieldbox from '../field-box';

import * as S from './styles';

import 'react-calendar/dist/Calendar.css';

const dateInputFormat = 'MMM dd, yyyy';

function DatepickerComponent(props) {
  const {
    field: {
      name,
      value,
    },
    field,
    label,
    minDate,
    maxDate,
    selectRange,
    onFocus,
    onBlur,
    onChange,
    format,
    shouldDisableDay,
    disableYearNavigation,
    disableViewChange,
    onMonthChange,
    allowDeselect,
    ...restProps
  } = props;

  const formatter = (val) => {
    if (!val) return '';

    const date = new Date(val);
    const formatted = formatDate(date, format || dateInputFormat);
    return formatted;
  };

  const formikContext = useFormikContext();

  const {
    setFieldTouched,
    setFieldValue,
  } = formikContext;
  const [isShown, setIsShown] = useState(false);

  const handleBlur = () => {
    setIsShown(false);
    onBlur();
    if (isShown) {
      setFieldTouched(name, true);
    }
  };

  const inputRef = useOutsideClick(() => {
    handleBlur();
  });

  const transitions = useTransition(isShown, {
    config: {
      duration: 210,
    },
    from: { opacity: 0, transform: 'translateY(20px)' },
    enter: { opacity: 1, transform: 'translateY(0)' },
    leave: { opacity: 0, transform: 'translateY(20px)' },
    onRest: () => setIsShown((prevIsShown) => (!!prevIsShown)),
  });

  const handleFocus = () => {
    setIsShown(true);
    onFocus();
  };

  const handleChange = (v) => {
    const newDate = new Date(v);
    const currentDate = value ? new Date(value) : null;

    if (
      allowDeselect
      && currentDate
      && newDate.getFullYear() === currentDate.getFullYear()
      && newDate.getMonth() === currentDate.getMonth()
      && newDate.getDate() === currentDate.getDate()
    ) {
      setFieldValue(name, '');
      onChange(null);
    } else {
      setFieldValue(name, newDate.toISOString());
      onChange(newDate);
    }

    handleBlur();
  };

  const handleBackspace = () => {
    setFieldValue(name, '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace') {
      handleBackspace(e);
    }
  };

  return (
    <div
      ref={inputRef}
    >
      <S.Input
        {...restProps}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onChange={noop}
        label={label}
        value={formatter(value)}
      />

      {transitions((styles, item) => item && (
        <S.PickerMenu style={styles}>
          <S.CalendarBox
            selectRange={selectRange}
            defaultValue={value ? new Date(value) : new Date()}
            minDate={minDate}
            maxDate={maxDate}
            onChange={handleChange}
            tileDisabled={({ date }) => shouldDisableDay(date)}
            view={disableViewChange ? 'month' : undefined}
            minDetail={disableViewChange ? 'month' : 'century'}
            maxDetail={disableViewChange ? 'month' : 'month'}
            next2Label={disableYearNavigation ? null : '»'}
            prev2Label={disableYearNavigation ? null : '«'}
            onActiveStartDateChange={({ activeStartDate }) => {
              onMonthChange(activeStartDate);
            }}
          />
        </S.PickerMenu>
      ))}
    </div>

  );
}

DatepickerComponent.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  label: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  selectRange: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  format: PropTypes.string,
  shouldDisableDay: PropTypes.func,
  disableYearNavigation: PropTypes.bool,
  disableViewChange: PropTypes.bool,
  onMonthChange: PropTypes.func,
  allowDeselect: PropTypes.bool,
};

DatepickerComponent.defaultProps = {
  label: '',
  minDate: null,
  maxDate: null,
  selectRange: false,
  onFocus: () => {},
  onBlur: () => {},
  onChange: () => {},
  format: '',
  shouldDisableDay: () => false,
  disableYearNavigation: false,
  disableViewChange: false,
  onMonthChange: () => {},
  allowDeselect: false,
};

export default function FormikDatepicker(props) {
  return (
    <Fieldbox
      {...props}
    >
      <DatepickerComponent {...props} />
    </Fieldbox>
  );
}
